import { useEffect, useState } from "react";
import { Eraser, X, Plus, Search } from "lucide-react";
import { PULP_OUTLINE_COLOR } from "./findingPalette.config";

// =====================================================================
// MobilePaletteSheet — iOS-style bottom sheet for the findings palette.
// =====================================================================
// Shown only on mobile / tablet (<lg). Wraps the same set of actions as
// the desktop FindingsPalette sidebar but renders them in a slide-up
// sheet so the chart can use the full screen width.
// =====================================================================
export default function MobilePaletteSheet({
  open,
  onClose,
  findings = [],
  active = null,
  disabled = false,
  onPickFinding,
  onPickEraser,
  onClear,
  onOpenAddCustom,
  canManageFindings = false,
}) {
  const [query, setQuery] = useState("");
  // Lock body scroll while the sheet is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc key dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const armedCode = active?.kind === "finding" ? active.code : null;
  const eraserArmed = active?.kind === "erase";
  const filteredFindings = query.trim()
    ? findings.filter((f) => String(f.display_name || f.name || f.code).toLowerCase().includes(query.trim().toLowerCase()))
    : findings;

  function pick(code) {
    onPickFinding?.(code);
    onClose?.();
  }
  function pickEraser() {
    onPickEraser?.();
    onClose?.();
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      data-testid="mobile-palette-sheet"
    >
      {/* Dimmed backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        className="relative w-full max-h-[85vh] bg-white rounded-t-3xl shadow-2xl flex flex-col animate-[slide-up_0.18s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-12 h-1 rounded-full bg-slate-300" aria-hidden />
        </div>

        {/* Title row */}
        <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100">
          <p className="text-sm font-bold tracking-wider uppercase text-slate-500">Findings</p>
          <button
            type="button"
            onClick={onClose}
            data-testid="mobile-palette-close"
            className="p-1.5 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-3 pt-3">
          <label className="relative block">
            <span className="sr-only">Search findings</span>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pens..."
              data-testid="mobile-findings-search"
              className="w-full h-10 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-500"
            />
          </label>
        </div>

        {/* Findings list */}
        <ul className="overflow-y-auto px-3 py-3 space-y-1.5 flex-1" data-testid="mobile-findings-list">
          {filteredFindings.map((f) => {
            const isArmed = armedCode === f.code;
            return (
              <li key={f.id ?? f.code}>
                <button
                  type="button"
                  onClick={() => pick(f.code)}
                  disabled={disabled}
                  data-testid={`mobile-finding-${f.code}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-base font-medium transition-colors ${
                    isArmed
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-800 bg-white hover:border-slate-400 active:bg-slate-50"
                  }`}
                >
                  <span
                    className="w-3.5 h-3.5 rounded-full shrink-0 ring-1 ring-black/10"
                    style={{
                      backgroundColor: f.color_hex,
                      border: f.is_pulp_involvement ? `1.5px solid ${PULP_OUTLINE_COLOR}` : "none",
                    }}
                  />
                  <span className="flex-1 text-left truncate">{f.display_name}</span>
                  <span className="text-[10px] uppercase tracking-wider opacity-70">
                    {f.requires_surfaces ? "surface" : "tooth"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Bottom action row */}
        <div className="px-3 pt-2 pb-[max(env(safe-area-inset-bottom),0.75rem)] border-t border-slate-100 bg-white flex items-center gap-2">
          <button
            type="button"
            onClick={pickEraser}
            disabled={disabled}
            data-testid="mobile-palette-eraser"
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
              eraserArmed
                ? "border-rose-600 bg-rose-50 text-rose-700"
                : "border-slate-200 text-slate-700 bg-white hover:border-rose-400 active:bg-rose-50"
            }`}
          >
            <Eraser className="w-4 h-4" /> Eraser
          </button>
          {(armedCode || eraserArmed) ? (
            <button
              type="button"
              onClick={() => { onClear?.(); onClose?.(); }}
              disabled={disabled}
              data-testid="mobile-palette-clear"
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Clear
            </button>
          ) : null}
          {canManageFindings ? (
            <button
              type="button"
              onClick={() => { onOpenAddCustom?.(); onClose?.(); }}
              disabled={disabled}
              data-testid="mobile-palette-add-custom"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-teal-400 hover:text-teal-600"
            >
              <Plus className="w-4 h-4" /> Custom
            </button>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
